import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { BarChart2, LogIn, Plus, Hash, Weight, DollarSign } from "lucide-react";
import { format, parseISO } from "date-fns";
import { utcEntryDay, calendarDayUtcStart } from "@/lib/trackerx-dates";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

interface Tracker {
  id: string;
  name: string;
  category: string;
  valueType: "number" | "weight" | "currency";
  unitLabel: string | null;
  period: "daily" | "weekly" | "monthly";
  goalValue: number | null;
  timeSlots: string[] | null;
  isActive: boolean;
}

interface TrackerEntry {
  id: string;
  trackerXId: string;
  date: string;
  timeSlot: string | null;
  value: number;
  notes: string | null;
}

function formatValue(value: number, valueType: string, unitLabel: string | null) {
  if (valueType === "currency") return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (valueType === "weight") return `${value}${unitLabel ?? "kg"}`;
  return `${value}${unitLabel ? ` ${unitLabel}` : ""}`;
}

function getTodayTotal(entries: TrackerEntry[]): number {
  const today = format(new Date(), "yyyy-MM-dd");
  return entries
    .filter((e) => utcEntryDay(e.date) === today)
    .reduce((sum, e) => sum + e.value, 0);
}

function LogDialog({
  tracker,
  open,
  onClose,
}: {
  tracker: Tracker;
  open: boolean;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [value, setValue] = useState("");
  const [timeSlot, setTimeSlot] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));

  const logMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", `/api/trackerx/${tracker.id}/entries`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/trackerx/${tracker.id}/entries`] });
      qc.invalidateQueries({ queryKey: ["/api/trackerx/all-entries"] });
      qc.invalidateQueries({ queryKey: ["/api/trackerx/widget-metrics"] });
      qc.invalidateQueries({ predicate: (q) => /trackerx-summary|habit-summaries/.test(String(q.queryKey[0])) });
      toast({ title: "Entry logged!" });
      setValue("");
      setNotes("");
      onClose();
    },
    onError: () => toast({ title: "Failed to log entry", variant: "destructive" }),
  });

  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!logMut.isPending) submitLockRef.current = false;
  }, [logMut.isPending]);

  const handleLog = () => {
    if (submitLockRef.current || logMut.isPending) return;
    if (!value.trim() || isNaN(Number(value))) {
      toast({ title: "Enter a valid number", variant: "destructive" });
      return;
    }
    submitLockRef.current = true;
    logMut.mutate({
      value: Number(value),
      // Calendar-date convention: send the picked day as UTC midnight.
      date: calendarDayUtcStart(date),
      timeSlot: timeSlot || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log — {tracker.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Value{tracker.unitLabel ? ` (${tracker.unitLabel})` : ""}</Label>
            <Input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0"
              autoFocus
            />
          </div>
          {tracker.timeSlots?.length ? (
            <div className="space-y-1">
              <Label>Time Slot</Label>
              <Select value={timeSlot || "none"} onValueChange={(v) => setTimeSlot(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="No slot" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No slot</SelectItem>
                  {tracker.timeSlots.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-1">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional…" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleLog} disabled={submitLockRef.current || logMut.isPending}>
            {logMut.isPending ? "Logging…" : "Log Entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function GoalTrackerXTab({ goalId }: { goalId: string }) {
  const [, navigate] = useLocation();
  const [loggingTracker, setLoggingTracker] = useState<Tracker | null>(null);

  const { data: trackers = [], isLoading } = useQuery<Tracker[]>({
    queryKey: [`/api/goals/${goalId}/trackerx`],
    queryFn: () =>
      apiRequest("GET", `/api/goals/${goalId}/trackerx`).then((r) => r.json()),
  });

  const entryResults = useQuery<Record<string, TrackerEntry[]>>({
    queryKey: ["/api/trackerx/goal-entries", goalId, trackers.map((t) => t.id).join(",")],
    queryFn: async () => {
      if (trackers.length === 0) return {};
      const pairs = await Promise.all(
        trackers.map((t) =>
          apiRequest("GET", `/api/trackerx/${t.id}/entries?limit=30`)
            .then((r) => r.json())
            .then((entries: TrackerEntry[]) => [t.id, entries] as const)
        )
      );
      return Object.fromEntries(pairs);
    },
    enabled: trackers.length > 0,
  });

  const allEntries = entryResults.data ?? {};

  if (isLoading) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Loading trackers…
      </div>
    );
  }

  if (trackers.length === 0) {
    return (
      <div className="py-12 flex flex-col items-center gap-3 text-center">
        <BarChart2 className="h-10 w-10 text-muted-foreground/30" />
        <p className="text-sm text-muted-foreground max-w-xs">
          No TrackerX trackers are linked to this goal yet. You can link trackers from the TrackerX page.
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate("/trackerx")} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Go to TrackerX
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {trackers.map((tracker) => {
        const entries = allEntries[tracker.id] ?? [];
        const todayTotal = getTodayTotal(entries);
        const pct = tracker.goalValue ? Math.min(100, (todayTotal / tracker.goalValue) * 100) : null;
        const lastEntry = entries.length > 0 ? entries[0] : null;

        const Icon =
          tracker.valueType === "currency"
            ? DollarSign
            : tracker.valueType === "weight"
            ? Weight
            : Hash;

        return (
          <Card key={tracker.id} className="overflow-hidden">
            <CardContent className="px-4 py-3 flex items-start gap-4">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-sm leading-none">{tracker.name}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{tracker.category} · {tracker.period}</p>
                  </div>
                  <Button size="icon" variant="outline" className="h-7 w-7 shrink-0" onClick={() => setLoggingTracker(tracker)}>
                    <LogIn className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold tabular-nums">
                    {formatValue(todayTotal, tracker.valueType, tracker.unitLabel)}
                  </span>
                  {tracker.goalValue != null && (
                    <span className="text-xs text-muted-foreground">
                      / {formatValue(tracker.goalValue, tracker.valueType, tracker.unitLabel)} today
                    </span>
                  )}
                </div>
                {pct !== null && <Progress value={pct} className="h-1.5" />}
                {lastEntry && (
                  <p className="text-[10px] text-muted-foreground">
                    Last logged: {formatValue(lastEntry.value, tracker.valueType, tracker.unitLabel)} on {format(parseISO(utcEntryDay(lastEntry.date)), "MMM d")}
                  </p>
                )}
                {tracker.timeSlots?.length ? (
                  <div className="flex flex-wrap gap-1 pt-0.5">
                    {tracker.timeSlots.map((slot) => {
                      const slotTotal = (allEntries[tracker.id] ?? [])
                        .filter((e) => utcEntryDay(e.date) === format(new Date(), "yyyy-MM-dd") && e.timeSlot === slot)
                        .reduce((s, e) => s + e.value, 0);
                      return (
                        <Badge key={slot} variant="outline" className="text-[10px] font-normal gap-1">
                          <span className="text-muted-foreground">{slot}</span>
                          <span className="font-semibold">{slotTotal}</span>
                        </Badge>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {loggingTracker && (
        <LogDialog
          tracker={loggingTracker}
          open={true}
          onClose={() => setLoggingTracker(null)}
        />
      )}
    </div>
  );
}
